// Sprint 11 UX.C2 — single source of truth for responsive breakpoints.
//
// Both the API (apps/api/src/services/responsiveSpecGenerator.ts) and the
// Web design-tokens module (apps/web/src/config/design-tokens.ts) import
// from here. Issue #576 AC: "Breakpoint constants sourced from design
// tokens (no hardcoded pixel values)" — anchoring the values in the shared
// package so the API and the rendered tokens stay in lockstep.
//
// Tailwind-aligned scale (Tailwind defaults: sm 640, md 768, lg 1024,
// xl 1280, 2xl 1536). The viewport buckets used by the responsive
// generator are derived from these:
//
//   mobile  : < md           (< 768px)
//   tablet  : md ≤ x < xl    (768 .. 1279px)
//   desktop : ≥ xl           (≥ 1280px)

export const RESPONSIVE_BREAKPOINTS = {
  sm:    '640px',
  md:    '768px',
  lg:    '1024px',
  xl:    '1280px',
  '2xl': '1536px',
} as const;

export type BreakpointToken = keyof typeof RESPONSIVE_BREAKPOINTS;

export type Viewport = 'mobile' | 'tablet' | 'desktop';

/** Pixel value (number, no `px` suffix) for a breakpoint token. */
export function breakpointPx(token: BreakpointToken): number {
  return Number(RESPONSIVE_BREAKPOINTS[token].replace('px', ''));
}

/**
 * Bucket a pixel width into one of the three viewport classes the
 * responsive generator targets. Anything below `md` is mobile; below
 * `xl` is tablet; ≥ `xl` is desktop. Used by tests + downstream
 * consumers that want to map a viewport to a pixel range without
 * re-doing the math.
 */
export function viewportForWidth(widthPx: number): Viewport {
  if (widthPx < breakpointPx('md')) return 'mobile';
  if (widthPx < breakpointPx('xl')) return 'tablet';
  return 'desktop';
}

/** Inclusive lower bound (px) for each viewport. */
export const VIEWPORT_RANGES: Readonly<Record<Viewport, { minPx: number; maxPx: number | null }>> = {
  mobile:  { minPx: 0,                       maxPx: breakpointPx('md') - 1 },
  tablet:  { minPx: breakpointPx('md'),      maxPx: breakpointPx('xl') - 1 },
  desktop: { minPx: breakpointPx('xl'),      maxPx: null },
};
